/**
 * Lane C0-FACE (live since 2026-10-10 through live.ts). Which face a person gets: sets per sex and age to pick from (eyes and brows
 * are the traced C0 features' variants, fine.ts), picked by a stable seed (the look's id, not its colours, so two
 * townsfolk dressed alike still differ), plus facial hair, marks and iris colour; hand-picked faces for the
 * player and the NPCs met most. See docs/research/C0_FACES.md.
 */
import type { Look } from '../../sprites';
import type { LabFace } from './draw';
import type { Age, Expr, Sex } from './parts';

type Slots = Pick<LabFace, 'eyes' | 'brows' | 'nose' | 'mouth' | 'jaw'>;

/** The parts each kind of person is picked from. */
export const SETS: Record<Sex, Record<Age, Record<keyof Slots, readonly string[]>>> = {
  female: {
    young: {
      eyes: ['doe', 'round', 'gentle', 'ref', 'lidded'],
      brows: ['thin', 'arch', 'willow', 'ref'],
      nose: ['dot', 'button'],
      mouth: ['petal', 'bow', 'smileF'],
      jaw: ['soft', 'round'],
    },
    adult: {
      eyes: ['ref', 'phoenix', 'cat', 'doe', 'cool', 'lidded', 'gentle'],
      brows: ['willow', 'arch', 'lifted', 'thin', 'ref'],
      nose: ['dot', 'button', 'bridge'],
      mouth: ['bow', 'pout', 'petal', 'smileF'],
      jaw: ['soft', 'round'],
    },
    elder: {
      eyes: ['aged', 'gentle', 'lidded'],
      brows: ['thin', 'worried', 'kind'],
      nose: ['button', 'broad'],
      mouth: ['petal', 'smileF'],
      jaw: ['soft', 'round'],
    },
  },
  male: {
    young: {
      eyes: ['ref', 'round', 'steady', 'droopy', 'fierce'],
      brows: ['ref', 'thick', 'sword', 'kind'],
      nose: ['dot', 'button'],
      mouth: ['small', 'grin', 'line'],
      jaw: ['soft', 'round'],
    },
    adult: {
      eyes: ['steady', 'sharp', 'hooded', 'narrow', 'fierce', 'droopy', 'ref'],
      brows: ['sword', 'blade', 'thick', 'bushy', 'knit', 'kind'],
      nose: ['bridge', 'broad', 'hook', 'button'],
      mouth: ['line', 'firm', 'frown', 'smirk', 'grin'],
      jaw: ['square', 'firm', 'broad', 'soft'],
    },
    elder: {
      eyes: ['agedFierce', 'aged', 'droopy', 'hooded'],
      brows: ['wildElder', 'longTail', 'bushy', 'blade'],
      nose: ['hook', 'broad', 'bridge'],
      mouth: ['frown', 'line', 'grin'],
      jaw: ['firm', 'soft', 'square'],
    },
  },
};

/** Facial hair for men picked by seed (live face.ts parts); repeated `none` weights the odds. */
const FACIAL: Record<'adult' | 'elder', readonly { beard: string; mustache: string }[]> = {
  // Only the live stamps that sit well on the grown head (stubble, chinstrap, short full beard and the handlebar
  // were drawn for the old head and read as stripes or rings on it: see docs/research/C0_FACES.md).
  adult: [
    { beard: 'none', mustache: 'none' },
    { beard: 'none', mustache: 'none' },
    { beard: 'none', mustache: 'none' },
    { beard: 'goatee', mustache: 'thin' },
    { beard: 'none', mustache: 'droopy' },
    { beard: 'none', mustache: 'thin' },
    { beard: 'goatee', mustache: 'none' },
  ],
  elder: [
    { beard: 'long', mustache: 'whiskers' },
    { beard: 'goatee', mustache: 'droopy' },
    { beard: 'wizard', mustache: 'dragon' },
    { beard: 'forked', mustache: 'thick' },
  ],
};

/** Marks picked by seed for adult men (most get none). */
const MARK_ODDS: readonly (readonly string[])[] = [[], [], [], [], [], [], ['mole'], ['scarCheek'], ['brow'], ['scarBrow']];

/** Iris colours for people whose look sets none: browns and near-black, rarely amber. */
const IRISES = ['#3a2418', '#4a2c1a', '#5a3a22', '#2a1a14', '#6a4424', '#3c2a20', '#7a4a1e'];

function hash(s: string): number {
  let h = 2166136261;
  for (const ch of s) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return h >>> 0;
}

function pickOf<T>(list: readonly T[], key: string): T {
  return list[hash(key) % list.length]!;
}

export const ageOf = (look: Look): Age =>
  look.age === 'elder' ? 'elder' : look.age === 'child' || look.age === 'teen' ? 'young' : 'adult';

/** A face picked from the sets by `seed`; each slot hashed on its own so faces mix freely. */
export function pickFace(sex: Sex, age: Age, seed: string): Slots & Pick<LabFace, 'beard' | 'mustache' | 'marks'> {
  const set = SETS[sex][age];
  const pick = (slot: keyof Slots) => pickOf(set[slot], `${slot}:${seed}`);
  const hairy =
    sex === 'male' && age !== 'young' ? pickOf(FACIAL[age], `hair:${seed}`) : { beard: 'none', mustache: 'none' };
  const marks = sex === 'male' && age === 'adult' ? pickOf(MARK_ODDS, `mark:${seed}`) : age === 'elder' ? ['forehead'] : [];
  return { eyes: pick('eyes'), brows: pick('brows'), nose: pick('nose'), mouth: pick('mouth'), jaw: pick('jaw'), ...hairy, marks };
}

/**
 * Hand-picked faces for the player as a man and a woman and the NPCs a player meets most, so the main cast never
 * repeats. Everyone else is picked by `pickFace`.
 */
export const NAMED: Record<string, Partial<LabFace>> = {
  // The player pair (lane C0-OUTFIT2X, user: man and woman clearly apart): a square jaw, thick straight sword brows and
  // keen eyes for him; big lashed eyes, thin arched willow brows and small tinted lips for her.
  hero: { eyes: 'sharp', brows: 'blade', nose: 'bridge', mouth: 'firm', jaw: 'square', marks: [], beard: 'none', mustache: 'none' },
  heroF: { eyes: 'doe', brows: 'willow', nose: 'button', mouth: 'bow', jaw: 'soft', marks: [], blush: true },
  elder: { age: 'elder', eyes: 'agedFierce', brows: 'wildElder', nose: 'hook', mouth: 'line', jaw: 'firm', marks: ['forehead'] },
  fisherOld: { eyes: 'droopy', brows: 'longTail', nose: 'broad', mouth: 'grin', jaw: 'square', lines: true, marks: ['forehead'] },
  scholar: { eyes: 'narrow', brows: 'thin', nose: 'bridge', mouth: 'firm', jaw: 'soft', marks: [], beard: 'none', mustache: 'none' },
  watchman: { eyes: 'hooded', brows: 'bushy', nose: 'broad', mouth: 'frown', jaw: 'broad', marks: ['brow'], beard: 'none', mustache: 'thick' },
  guardOfficer: { eyes: 'sharp', brows: 'blade', nose: 'bridge', mouth: 'firm', jaw: 'square', marks: [], beard: 'none', mustache: 'none' },
  bandit: { eyes: 'fierce', brows: 'knit', nose: 'hook', mouth: 'smirk', jaw: 'broad', lines: true, marks: ['scarBrow'], beard: 'goatee', mustache: 'droopy' },
  stationMaster: { eyes: 'droopy', brows: 'kind', nose: 'button', mouth: 'grin', jaw: 'round', lines: true, marks: [], beard: 'none', mustache: 'droopy' },
  taoist: { eyes: 'narrow', brows: 'sword', nose: 'bridge', mouth: 'line', jaw: 'firm', marks: [], beard: 'goatee', mustache: 'thin' },
  monk: { eyes: 'gentle', brows: 'kind', nose: 'broad', mouth: 'grin', jaw: 'round', marks: [], beard: 'none', mustache: 'none' },
  teaSeller: { eyes: 'gentle', brows: 'arch', nose: 'button', mouth: 'smileF', jaw: 'round', marks: [] },
  fisherKid: { eyes: 'round', brows: 'thick', nose: 'dot', mouth: 'grin', jaw: 'round', blush: true, marks: [] },
  farmer: { eyes: 'ref', brows: 'thick', nose: 'broad', mouth: 'line', jaw: 'square', lines: true, marks: ['mole'], beard: 'none', mustache: 'none' },
  farmerAunt: { eyes: 'lidded', brows: 'thin', nose: 'broad', mouth: 'pout', jaw: 'round', lines: true, marks: [] },
  lilac: { eyes: 'doe', brows: 'arch', nose: 'dot', mouth: 'petal', jaw: 'soft', marks: [] },
  blissLady: { eyes: 'cool', brows: 'lifted', nose: 'dot', mouth: 'pout', jaw: 'soft', marks: ['mole'] },
};

/** The game's own face lists (render/rig/sets.ts `FACES`): the old style picks only from these. */
const LIVE: Record<Sex, Record<'eyes' | 'brows' | 'nose' | 'mouth', readonly string[]>> = {
  male: {
    eyes: ['ref', 'phoenix', 'narrow', 'fierce', 'gentle'],
    brows: ['ref', 'sword', 'thick', 'sword'],
    nose: ['ref', 'high', 'broad'],
    mouth: ['ref', 'stern', 'wide'],
  },
  female: {
    eyes: ['ref', 'phoenix', 'round', 'gentle'],
    brows: ['ref', 'arch', 'thin', 'worried'],
    nose: ['ref', 'small', 'high'],
    mouth: ['ref', 'full', 'smile'],
  },
};

/**
 * The old style's parts: the game's own pick (sets.ts `faceOf`, keyed on the look's colours), or, `byId`, a pick from
 * the same lists by the person's id, so people dressed alike still differ (the player always keeps the game's).
 */
function oldParts(id: string, look: Look, sex: Sex, byId: boolean): Record<'eyes' | 'brows' | 'nose' | 'mouth', string> {
  const who = !byId
    ? `${look.skin}|${look.hair}|${look.hairStyle}|${look.eye}|${look.iris ?? ''}|${look.female ? 1 : 0}`
    : `id:${id}`;
  const pick = (slot: 'eyes' | 'brows' | 'nose' | 'mouth') => pickOf(LIVE[sex][slot], `${slot}:${who}`);
  return { eyes: pick('eyes'), brows: pick('brows'), nose: pick('nose'), mouth: pick('mouth') };
}

/** The face for a look (by id): elders get the new set, everyone else the game's face sharpened (`style`). */
export function faceFor(
  id: string,
  look: Look,
  expr: Expr | 'auto' = 'auto',
  /** Old style: `live` keeps the game's own pick (for side-by-side review); `id` picks per person (the proposal). */
  seed: 'live' | 'id' = 'live',
  /**
   * `auto`: the age switch (elders new, others old sharpened). `elderStyle` (user sample, 2026-10-10): young and
   * adult people in the elders' style — the new eye and brow variants, noses, mouths and jaws — without age cues
   * (no wrinkles, bags, folds or frown lines; brows in the hair colour; blush for women and the young).
   */
  mode: 'auto' | 'elderStyle' = 'auto',
): LabFace {
  const sex: Sex = look.female ? 'female' : 'male';
  // The player's looks (hero, heroF, heroPalm, the school uniforms heroWudang…) all keep the player's own face.
  const named = NAMED[id] ?? (id.startsWith('hero') ? NAMED[look.female ? 'heroF' : 'hero'] : undefined);
  const age = named?.age ?? ageOf(look);
  if (age !== 'elder' && mode === 'auto')
    return {
      sex,
      age,
      style: 'old',
      ...oldParts(id, look, sex, seed === 'id' && !id.startsWith('hero')),
      jaw: 'soft',
      blush: !!look.blush,
      ...(look.blush ? { blushColour: look.blush } : {}),
      iris: look.iris ?? look.eye,
      ...(look.beardColor ? { brow: look.beardColor } : {}),
      beard: look.beard === 'long' ? 'long' : 'none',
      mustache: look.beard === 'long' ? 'whiskers' : 'none',
      sideburns: look.beard === 'stubble' ? 'short' : 'none',
      marks: [],
      lip: look.mouth,
      expr,
    };
  const picked = pickFace(sex, age, id);
  // A look that sets its own facial hair keeps it.
  const own = look.beard
    ? {
        beard: look.beard === 'long' ? 'long' : 'none',
        mustache: look.beard === 'long' ? 'whiskers' : 'none',
        sideburns: look.beard === 'stubble' ? 'short' : 'none',
      }
    : {};
  const face: LabFace = {
    sex,
    age,
    style: 'new',
    ...picked,
    ...named,
    ...own,
    blush: named?.blush ?? (mode === 'elderStyle' && (sex === 'female' || age === 'young')),
    iris: look.iris ?? pickOf(IRISES, `iris:${id}`),
    ...(look.beardColor ? { brow: look.beardColor } : {}),
    lip: look.mouth,
    expr,
  };
  if (age === 'elder') return face;
  // Elder style on the young: no age cues.
  return { ...face, lines: false, marks: (face.marks ?? []).filter((m) => m !== 'forehead' && m !== 'brow') };
}
